/**
 * Dates calendaires au format "YYYY-MM-DD", sans fuseau horaire.
 * Tous les calculs passent par UTC pour éviter les surprises des changements d'heure.
 */

export type DateKey = string;

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

export function isDateKey(value: unknown): value is DateKey {
  if (typeof value !== "string") return false;
  const match = DATE_RE.exec(value);
  if (!match) return false;
  const [, y, m, d] = match.map(Number) as [number, number, number, number];
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}

export function isTimeKey(value: unknown): value is string {
  return typeof value === "string" && TIME_RE.test(value);
}

function toUtc(key: DateKey): Date {
  const [y, m, d] = key.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d));
}

function fromUtc(date: Date): DateKey {
  return date.toISOString().slice(0, 10);
}

/** Date du jour dans le fuseau du navigateur ou du serveur. */
export function todayKey(now: Date = new Date()): DateKey {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function addDays(key: DateKey, days: number): DateKey {
  const date = toUtc(key);
  date.setUTCDate(date.getUTCDate() + days);
  return fromUtc(date);
}

/**
 * Ajoute des mois en restant dans le mois cible :
 * 31 août + 6 mois = 28 (ou 29) février, et non 3 mars.
 */
export function addMonthsClamped(key: DateKey, months: number): DateKey {
  const [y, m, d] = key.split("-").map(Number) as [number, number, number];
  const targetMonthIndex = m - 1 + months;
  const target = new Date(Date.UTC(y, targetMonthIndex, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(d, lastDay));
  return fromUtc(target);
}

/**
 * Ajoute une fréquence exprimée en mois, éventuellement fractionnaire :
 * 3,5 mois = 3 mois + 15 jours.
 */
export function addFrequency(key: DateKey, frequencyMonths: number): DateKey {
  const whole = Math.trunc(frequencyMonths);
  const extraDays = Math.round((frequencyMonths - whole) * 30);
  return addDays(addMonthsClamped(key, whole), extraDays);
}

/** 0 = dimanche … 6 = samedi */
export function weekday(key: DateKey): number {
  return toUtc(key).getUTCDay();
}

/** Décale un samedi ou un dimanche au lundi suivant. */
export function nextWorkday(key: DateKey): DateKey {
  const day = weekday(key);
  if (day === 6) return addDays(key, 2);
  if (day === 0) return addDays(key, 1);
  return key;
}

/** Lundi de la semaine contenant la date. */
export function startOfWeek(key: DateKey): DateKey {
  const day = weekday(key);
  const offset = day === 0 ? -6 : 1 - day;
  return addDays(key, offset);
}

export function startOfMonth(key: DateKey): DateKey {
  return `${key.slice(0, 7)}-01`;
}

export function endOfMonth(key: DateKey): DateKey {
  return addDays(addMonthsClamped(startOfMonth(key), 1), -1);
}

export function diffDays(from: DateKey, to: DateKey): number {
  return Math.round((toUtc(to).getTime() - toUtc(from).getTime()) / 86_400_000);
}

export function compareDateKeys(a: DateKey, b: DateKey): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Ajoute des minutes à une heure "HH:MM" ; renvoie null si l'on dépasse minuit. */
export function addMinutesToTime(time: string, minutes: number): string | null {
  const [h, m] = time.split(":").map(Number) as [number, number];
  const total = h * 60 + m + minutes;
  if (total >= 24 * 60) return null;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(Math.floor(total / 60))}:${pad(total % 60)}`;
}

export function timeToMinutes(time: string): number {
  const [h, m] = time.split(":").map(Number) as [number, number];
  return h * 60 + m;
}

const WEEKDAYS_SHORT = ["dim.", "lun.", "mar.", "mer.", "jeu.", "ven.", "sam."];
const WEEKDAYS_LONG = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
const MONTHS_LONG = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre"
];
const MONTHS_SHORT = [
  "janv.", "févr.", "mars", "avr.", "mai", "juin",
  "juil.", "août", "sept.", "oct.", "nov.", "déc."
];

export function formatDateLong(key: DateKey): string {
  const [y, m, d] = key.split("-").map(Number) as [number, number, number];
  return `${WEEKDAYS_LONG[weekday(key)]} ${d} ${MONTHS_LONG[m - 1]} ${y}`;
}

export function formatDateShort(key: DateKey, withYear = false): string {
  const [y, m, d] = key.split("-").map(Number) as [number, number, number];
  return `${WEEKDAYS_SHORT[weekday(key)]} ${d} ${MONTHS_SHORT[m - 1]}${withYear ? ` ${y}` : ""}`;
}

export function formatMonth(key: DateKey): string {
  const [y, m] = key.split("-").map(Number) as [number, number];
  return `${MONTHS_LONG[m - 1]} ${y}`;
}

export function weekdayShort(key: DateKey): string {
  return WEEKDAYS_SHORT[weekday(key)]!;
}

/** "aujourd'hui", "demain", "hier", "dans 3 j", "il y a 5 j" */
export function formatRelativeDay(key: DateKey, today: DateKey): string {
  const diff = diffDays(today, key);
  if (diff === 0) return "aujourd'hui";
  if (diff === 1) return "demain";
  if (diff === -1) return "hier";
  if (diff > 0) return `dans ${diff} j`;
  return `il y a ${-diff} j`;
}
